import { api } from '../api.ts';
import { html, mount, raw } from '../html.ts';
import { currentIntlLocale, formatDate, GATE_STATUS, PHASE_STATUS, t, TASK_STATUS, today } from '../i18n.ts';
import { emptyState } from '../ui.ts';

/**
 * Project schedule (Gantt-lite): phases as bars, gates as milestones and dated
 * tasks as points on one time axis with month ticks and a today line. Every
 * item is a real list entry with its dates in text; the positioning is purely
 * visual. App-local prototype of a Purple UI "timeline chart".
 */

const DAY = 86_400_000;
const toTime = (date: string) => new Date(`${date}T00:00:00Z`).getTime();

interface Item {
  kind: 'phase' | 'gate' | 'task';
  label: string;
  start: string;
  end: string;
  status: string;
  detail: string;
}

export async function renderTimeline(target: HTMLElement, projectId: string, project: { starts_on: string | null; ends_on: string | null; key: string }) {
  const [phases, gates, tasks] = await Promise.all([
    api.get(`/projects/${projectId}/phases`),
    api.get(`/projects/${projectId}/gates`),
    api.get(`/projects/${projectId}/tasks?limit=500`),
  ]);
  const items: Item[] = [];
  for (const p of phases.items) {
    if (!p.starts_on && !p.ends_on) continue;
    const start = p.starts_on ?? p.ends_on;
    items.push({ kind: 'phase', label: p.name, start, end: p.ends_on ?? start, status: p.status, detail: PHASE_STATUS[p.status] ?? p.status });
  }
  for (const g of gates.items) {
    if (!g.due_on) continue;
    items.push({ kind: 'gate', label: `${g.code} · ${g.name}`, start: g.due_on, end: g.due_on, status: g.status, detail: GATE_STATUS[g.status] ?? g.status });
  }
  for (const task of tasks.items) {
    if (!task.due_on || task.status === 'cancelled') continue;
    items.push({
      kind: 'task',
      label: `${project.key}-${task.number} ${task.title}`,
      start: task.starts_on && task.starts_on < task.due_on ? task.starts_on : task.due_on,
      end: task.due_on,
      status: task.status,
      detail: TASK_STATUS[task.status] ?? task.status,
    });
  }
  if (!items.length) {
    mount(target, emptyState(t('Noch kein Zeitplan'), t('Phasen mit Zeitraum, Gates mit Fälligkeit und Aufgaben mit Termin erscheinen hier auf einer Zeitachse.')));
    return;
  }

  const now = today();
  const dates = items.flatMap((i) => [i.start, i.end]).concat([now, project.starts_on, project.ends_on].filter(Boolean) as string[]);
  const min = toTime(dates.reduce((a, b) => (a < b ? a : b)));
  const max = toTime(dates.reduce((a, b) => (a > b ? a : b)));
  const from = min - 3 * DAY;
  const span = Math.max(max + 3 * DAY - from, 14 * DAY);
  const pct = (date: string) => ((toTime(date) - from) / span) * 100;

  const months: { label: string; left: number }[] = [];
  const cursor = new Date(from);
  cursor.setUTCDate(1);
  cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  const monthFormat = new Intl.DateTimeFormat(currentIntlLocale(), { month: 'short', year: '2-digit', timeZone: 'UTC' });
  while (cursor.getTime() < from + span) {
    months.push({ label: monthFormat.format(cursor), left: ((cursor.getTime() - from) / span) * 100 });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  const groups: [Item['kind'], string][] = [
    ['phase', t('Phasen')],
    ['gate', t('Gates')],
    ['task', t('Aufgaben mit Termin')],
  ];
  const sorted = (kind: Item['kind']) => items.filter((i) => i.kind === kind).sort((a, b) => a.start.localeCompare(b.start));
  const overdue = (i: Item) => i.kind === 'task' && i.end < now && i.status !== 'done';

  mount(
    target,
    html`<figure class="pma-gantt" aria-labelledby="gantt-title">
      <figcaption id="gantt-title" class="pma-muted pma-small">${t('Zeitachse {from} – {to} · heute {today}', { from: formatDate(new Date(from).toISOString().slice(0, 10)), to: formatDate(new Date(from + span).toISOString().slice(0, 10)), today: formatDate(now) })}</figcaption>
      <div class="pma-gantt__axis" aria-hidden="true">${months.map((m) => html`<span style="left:${m.left.toFixed(2)}%">${m.label}</span>`)}</div>
      ${groups.map(([kind, title]) => {
        const rows = sorted(kind);
        if (!rows.length) return '';
        return html`<section class="pma-gantt__group" aria-label="${title}">
          <h3 class="pma-gantt__title">${title}</h3>
          <ol class="pma-gantt__rows">${rows.map((i) => {
            const left = pct(i.start);
            const width = Math.max(pct(i.end) - left, i.kind === 'phase' ? 0.8 : 0);
            return html`<li class="pma-gantt__row">
              <span class="pma-gantt__label">${i.label}</span>
              <span class="pma-gantt__track">
                <span class="pma-gantt__today" style="left:${pct(now).toFixed(2)}%" aria-hidden="true"></span>
                <span class="pma-gantt__mark pma-gantt__mark--${i.kind} pma-gantt__mark--${i.status}${overdue(i) ? ' pma-gantt__mark--overdue' : ''}"
                  style="left:${left.toFixed(2)}%;${i.kind === 'phase' || (i.kind === 'task' && width > 0) ? `width:${width.toFixed(2)}%` : ''}"
                  title="${i.label}: ${i.start === i.end ? formatDate(i.end) : `${formatDate(i.start)} – ${formatDate(i.end)}`} · ${i.detail}"></span>
              </span>
              <span class="pma-gantt__dates pma-small">${i.start === i.end ? formatDate(i.end) : raw(`${formatDate(i.start)} – ${formatDate(i.end)}`)} · <span class="${overdue(i) ? 'pma-overdue' : 'pma-muted'}">${i.detail}</span></span>
            </li>`;
          })}</ol>
        </section>`;
      })}
    </figure>`,
  );
}
